import React, { useEffect, useMemo, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { buildPreviewSegments } from "../previewSegmentUtils";
import { unescapeDisplayMd } from "../writingAssistantUtils";
import WritingPreviewChangeCallout from "./WritingPreviewChangeCallout";
import { writingPreviewMarkdownComponents } from "./writingPreviewMarkdownComponents";

function scrollCalloutIntoView(container, el) {
	const elRect = el.getBoundingClientRect();
	const containerRect = container.getBoundingClientRect();
	const scrollTop =
		container.scrollTop +
		(elRect.top - containerRect.top) -
		container.clientHeight / 2 +
		elRect.height / 2;
	container.scrollTo({ top: Math.max(0, scrollTop), behavior: "auto" });
}

function pickClosestVisibleChangeIndex(container) {
	const containerRect = container.getBoundingClientRect();
	const centerY = containerRect.top + containerRect.height / 2;
	const bandTop = containerRect.top + containerRect.height * 0.35;
	const bandBottom = containerRect.bottom - containerRect.height * 0.35;

	let bestIndex = null;
	let bestDistance = Infinity;

	container.querySelectorAll("[data-change-index]").forEach((el) => {
		const rect = el.getBoundingClientRect();
		const entryCenterY = rect.top + rect.height / 2;
		const inCenterBand = entryCenterY >= bandTop && entryCenterY <= bandBottom;
		if (!inCenterBand) return;

		const distance = Math.abs(entryCenterY - centerY);
		if (distance < bestDistance) {
			bestDistance = distance;
			const idx = Number(el.getAttribute("data-change-index"));
			if (!Number.isNaN(idx)) bestIndex = idx;
		}
	});

	return bestIndex;
}

export default function WritingPreviewPane({
	changes,
	originalMd,
	patchedMd,
	activeChangeIndex,
	scrollRequestId,
	latestValidation,
	onChangeVisible,
	changeRefs,
	suppressScrollSyncRef,

	// Edit plumbing (optional) – forwarded to each WritingPreviewChangeCallout
	editingEnabled = false,
	isSavingAll = false,
	isSavingThisAtIndex,
	getDraftValueForIndex,
	isDirtyAtIndex,
	isIndexSaved,
	getInlineRejectionForIndex,
	onEditToggleAtIndex,
	onDraftChangeAtIndex,
	onClearDraftAtIndex,
	onSaveSingleAtIndex,
}) {
	const scrollContainerRef = useRef(null);
	const observerRafRef = useRef(null);

	const segments = useMemo(
		() => buildPreviewSegments(originalMd, changes, patchedMd),
		[originalMd, changes, patchedMd]
	);

	useEffect(() => {
		const root = scrollContainerRef.current;
		if (!root || !changes?.length) return undefined;

		const anchors = root.querySelectorAll("[data-change-index]");
		if (anchors.length === 0) return undefined;

		const observer = new IntersectionObserver(
			() => {
				if (suppressScrollSyncRef?.current) return;

				if (observerRafRef.current != null) {
					cancelAnimationFrame(observerRafRef.current);
				}

				observerRafRef.current = requestAnimationFrame(() => {
					observerRafRef.current = null;
					const idx = pickClosestVisibleChangeIndex(root);
					if (idx != null) onChangeVisible(idx);
				});
			},
			{ root, threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: "-35% 0px -35% 0px" }
		);

		anchors.forEach((el) => observer.observe(el));
		return () => {
			if (observerRafRef.current != null) {
				cancelAnimationFrame(observerRafRef.current);
			}
			observer.disconnect();
		};
	}, [changes?.length, onChangeVisible, segments, suppressScrollSyncRef]);

	useEffect(() => {
		if (scrollRequestId === 0 || activeChangeIndex < 0) return;

		const container = scrollContainerRef.current;
		if (!container) return;

		requestAnimationFrame(() => {
			const el =
				changeRefs.current?.[activeChangeIndex] ??
				container.querySelector(`[data-change-index="${activeChangeIndex}"]`);
			if (el) scrollCalloutIntoView(container, el);
		});
	}, [scrollRequestId, activeChangeIndex, changeRefs]);

	return (
		<div className="writing-preview__pane">
			<div className="writing-preview__pane-card" ref={scrollContainerRef}>
				<div className="writing-preview__markdown">
					{segments.map((segment, index) => {
						if (segment.type === "markdown") {
							return (
								<ReactMarkdown
									key={`md-${index}`}
									components={writingPreviewMarkdownComponents}
								>
									{segment.content}
								</ReactMarkdown>
							);
						}

						if (segment.type === "additions-header") {
							return (
								<div key="additions-header" className="writing-preview__ai-additions">
									<h3 className="writing-preview__ai-additions-title">AI additions</h3>
									<p className="writing-preview__ai-additions-subtitle text-muted">
										These were inserted outside the main page body (e.g. in the site header
										or menu), so they appear here. Inserts placed in the page content
										itself show inline at the correct position.
									</p>
								</div>
							);
						}

						if (segment.type === "callout") {
							const idx = segment.changeIndex;
							const rawChange = changes?.[idx];
							const deploymentStatus = String(
								rawChange?.deployment_status || ""
							).toLowerCase();
							const canEditChange =
								rawChange?.allow_edit !== false &&
								deploymentStatus !== "awaiting_review";
							const rawMd = rawChange?.new_content_md;
							const fallbackMd = unescapeDisplayMd(
								typeof rawMd === "string" ? rawMd : null,
								rawChange?.new_content_text || ""
							);
							// Every lookup below uses strict-normalized fallbacks to prevent
							// spurious truthy values on a fresh page load before the hook
							// has finished initializing its state.
							const _rawDraft =
								editingEnabled && typeof getDraftValueForIndex === "function"
									? getDraftValueForIndex(idx)
									: null;
							const draftValue =
								typeof _rawDraft === "string" ? _rawDraft : null;
							const isDirty = Boolean(
								editingEnabled &&
									typeof isDirtyAtIndex === "function" &&
									isDirtyAtIndex(idx, fallbackMd)
							);
							const isSaved = Boolean(
								editingEnabled &&
									typeof isIndexSaved === "function" &&
									isIndexSaved(idx)
							);
							const _rawRej =
								editingEnabled && typeof getInlineRejectionForIndex === "function"
									? getInlineRejectionForIndex(idx)
									: null;
							const rejectionMessage =
								typeof _rawRej === "string" && _rawRej.length > 0 ? _rawRej : null;
							// Extra belt-and-suspenders guard: even if the callback has a bug,
							// never treat index=0 as "saving" unless we have a genuine truthy result
							// from a callback that definitely exists and returns true.
							const _rawSavingThis =
								editingEnabled && typeof isSavingThisAtIndex === "function"
									? isSavingThisAtIndex(idx)
									: false;
							const isSavingThis = _rawSavingThis === true;

							return (
								<WritingPreviewChangeCallout
									key={`callout-${segment.changeIndex}`}
									change={segment.change}
									changeIndex={idx}
									isActive={activeChangeIndex === idx}
									latestValidation={latestValidation}
									innerRef={(el) => {
										if (changeRefs.current && el) {
											changeRefs.current[idx] = el;
										}
									}}
									editable={editingEnabled && canEditChange}
									isSavingAll={isSavingAll}
									isSavingThis={isSavingThis}
									draftValue={draftValue}
									isDirty={isDirty}
									isSaved={isSaved}
									rejectionMessage={rejectionMessage}
									onEditToggle={editingEnabled ? onEditToggleAtIndex : undefined}
									onDraftChange={editingEnabled ? onDraftChangeAtIndex : undefined}
									onClearDraft={editingEnabled ? onClearDraftAtIndex : undefined}
									onSaveSingle={editingEnabled ? onSaveSingleAtIndex : undefined}
								/>
							);
						}

						return null;
					})}
				</div>
			</div>
			<p className="writing-preview__pane-footnote text-muted small mb-0">
				Main content only — header, footer, and navigation removed.
			</p>
		</div>
	);
}
